import React, { useEffect, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import { Copy, HandThumbsUp, HandThumbsDown } from 'react-bootstrap-icons';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import './component/Room.css';
import './component/ChatAccessibility.css';
import './App.css';
import './kbEmptyBanner.css';
import { useCreateNewRoomChatMutation, useUpdateChatFeedbackMutation, userApi } from './services/user';
import { authApi, useGetUserDataQuery } from './services/auth';
import { sendStreamMessage } from './services/streamApi';
import copy from 'copy-to-clipboard';
import ChatHeader from './component/ChatHeader';
import PlanBanner, { ASK_BLOCKED_MESSAGE_ID } from './component/PlanBanner';
import ThinkingIndicator from './component/ThinkingIndicator';
import { useDispatch, useSelector } from 'react-redux';
import { stripMarkdownForCopy, validateAnalysisFile, getAnalysisPlaceholder } from './utils/chatUi';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Pencil } from 'lucide-react';
import { markdownComponents } from './utils/markdownComponents';
import Citations, { ReviewedAnswerBadge } from './Citations';

const SUGGESTED_QUESTIONS = [
  'What changed in Absence Management for 26C?',
  'How do I configure payroll costing for a new legal entity?',
  'Troubleshoot a failed payroll flow',
];

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour >= 5 && hour <= 11) return 'Good morning';
  if (hour >= 12 && hour <= 16) return 'Good afternoon';
  if (hour >= 17 && hour <= 21) return 'Good evening';
  return 'Welcome back';
};

// Typing Animation Component
const TypingAnimation = ({ text, speed = 30 }) => {
  const [displayedText, setDisplayedText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (currentIndex < text.length) {
      const timeout = setTimeout(() => {
        setDisplayedText(prev => prev + text[currentIndex]);
        setCurrentIndex(prev => prev + 1);
      }, speed);
      return () => clearTimeout(timeout);
    }
  }, [currentIndex, text, speed]);

  useEffect(() => {
    setDisplayedText('');
    setCurrentIndex(0);
  }, [text]);

  return (
    <ReactMarkdown
      components={{
        a: ({ node, ...props }) => (
          <a {...props} target="_blank" rel="noopener noreferrer" />
        ),
      }}
    >
      {displayedText}
    </ReactMarkdown>
  );
};

const ChatWindow = ({ activeChat }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const { selectedDB, selectedModule } = useSelector((state) => state.global);
  const inputPlaceholder = 'Ask anything about Oracle HCM…';

  const [createNewRoomChat, { error }] = useCreateNewRoomChatMutation();
  const { data: userData } = useGetUserDataQuery();
  const firstName = userData?.first_name || '';
  const canAsk = userData?.can_ask !== false;
  const [isStreaming, setIsStreaming] = useState(false);
  const [updateChatFeedback] = useUpdateChatFeedbackMutation();
  const isSendingRef = useRef(false);
  const textareaRef = useRef(null);
  const chatContainerRef = useRef(null);
  const userScrolledUp = useRef(false);
  const lastScrollTop = useRef(0);
  const accumulatedText = useRef('');
  const streamingMessageIdRef = useRef(null);
  const citationsRef = useRef([]);
  const hasTenantDocsRef = useRef(false);
  const answerSourceRef = useRef('ai_knowledge');
  const fileInputRef = useRef(null);
  const analyzeAbortRef = useRef(null);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editText, setEditText] = useState('');
  const [citations, setCitations] = useState([]);
  const [hasTenantDocs, setHasTenantDocs] = useState(false);
  const [answerSource, setAnswerSource] = useState('ai_knowledge');
  const [analysisFile, setAnalysisFile] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [kbStatus, setKbStatus] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [welcomeInputFocused, setWelcomeInputFocused] = useState(false);

  const apiDomain = (process.env.REACT_APP_BASE_URL || '')
    .replace(/\/apis\/?$/, '')
    .replace(/\/$/, '');

  // New chat route: never load history; keep messages empty
  useEffect(() => {
    setMessages([]);
    setInput('');
    isSendingRef.current = false;
  }, []);

  useEffect(() => {
    const fetchKbStatus = async () => {
      const baseUrl = (process.env.REACT_APP_BASE_URL || '').replace(/\/$/, '');
      const token = localStorage.getItem('access_token');
      try {
        const res = await fetch(`${baseUrl}/tenant/knowledge-base/status/`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (res.ok) {
          const data = await res.json();
          setKbStatus(data);
        }
      } catch (_) {
        // ignore
      }
    };
    fetchKbStatus();
  }, []);

  useEffect(() => {
    if (error) {
      toast.error(error.data?.error || 'An unexpected error occurred.');
    }
  }, [error]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
  }, [input]);

  // Abort in-flight document analysis on unmount
  useEffect(() => {
    return () => {
      if (analyzeAbortRef.current) {
        analyzeAbortRef.current.abort();
      }
    };
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleScroll = () => {
    const container = chatContainerRef.current;
    if (!container) return;
    const isNearBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight < 100;
    userScrolledUp.current = !isNearBottom;
    lastScrollTop.current = container.scrollTop;
  };

  const scrollToBottom = (force = false) => {
    const container = chatContainerRef.current;
    if (!container) return;
    if (force || !userScrolledUp.current) {
      container.scrollTop = container.scrollHeight;
    }
  };

  useEffect(() => {
    if (messages.length > 0 || citations.length > 0) {
      scrollToBottom();
    }
  }, [messages, isStreaming, citations]);

  const handleSendMessage = async (forcedText = null) => {
    const messageText = (forcedText ?? input).trim();
    if (!messageText && !analysisFile) return;
    if (!canAsk) return;
    if (isSendingRef.current || isStreaming || isAnalyzing) return;

    if (analysisFile && !messageText) {
      toast.info('Type a question about your document');
      return;
    }

    if (analysisFile && messageText) {
      await handleAnalyzeDocument(messageText);
      return;
    }

    isSendingRef.current = true;
    try {
    setCitations([]);
    setHasTenantDocs(false);
    setAnswerSource('ai_knowledge');
    citationsRef.current = [];
    hasTenantDocsRef.current = false;
    answerSourceRef.current = 'ai_knowledge';
    userScrolledUp.current = false;
    setMessages((prev) => [...prev, { sender: 'user', text: messageText, id: `user-${Date.now()}` }]);
    if (forcedText == null) setInput('');

    const placeholderId = `stream-${Date.now()}`;
    accumulatedText.current = '';
    streamingMessageIdRef.current = placeholderId;
    setMessages((prev) => [...prev, { sender: 'bot', text: '', id: placeholderId, feedback: null, streaming: true, citations: [], hasTenantDocs: false, answerSource: 'ai_knowledge', suggestions: [] }]);
    setIsStreaming(true);
    scrollToBottom(true);

    const streamedOk = await sendStreamMessage(
      { user_question: messageText, database_id: selectedDB, module_hint: selectedModule },
      {
        onToken: (token) => {
          accumulatedText.current += token;
          setMessages((prev) =>
            prev.map((m) => (m.id === placeholderId ? { ...m, text: (m.text || '') + token } : m))
          );
          scrollToBottom();
        },
        onDone: (data) => {
          const streamId = streamingMessageIdRef.current;
          const finalText = accumulatedText.current || 'No response received.';
          setMessages((prev) => {
            const exists = prev.find((m) => m.id === streamId);
            if (!exists) {
              return [...prev, {
                sender: 'bot',
                text: finalText,
                id: data.chat_id,
                feedback: null,
                streaming: false,
                download_url: data.download_url,
                citations: [],
                hasTenantDocs: false,
                answerSource: 'ai_knowledge',
                suggestions: [],
              }];
            }
            return prev.map((m) =>
              m.id === streamId
                ? {
                    ...m,
                    id: data.chat_id,
                    streaming: false,
                    text: finalText,
                    download_url: data.download_url,
                    // Preserve citations that arrived via onCitations before onDone
                    citations: m.citations?.length > 0 ? m.citations : [],
                    hasTenantDocs: m.hasTenantDocs ?? false,
                    answerSource: m.answerSource ?? 'ai_knowledge',
                  }
                : m
            );
          });
          setIsStreaming(false);
          streamingMessageIdRef.current = null;
          accumulatedText.current = '';
          scrollToBottom(true);
          dispatch(userApi.util.invalidateTags(['NEW_CHAT']));
          navigate(`/${data.room_id}`, {
            replace: true,
            state: {
              citations: citationsRef.current,
              hasTenantDocs: hasTenantDocsRef.current,
              answerSource: answerSourceRef.current,
            },
          });
        },
        onCitations: (parsed) => {
          citationsRef.current = parsed.citations || [];
          hasTenantDocsRef.current = parsed.has_tenant_docs || false;
          answerSourceRef.current = parsed.answer_source || 'ai_knowledge';
          setCitations(parsed.citations || []);
          setHasTenantDocs(parsed.has_tenant_docs || false);
          setAnswerSource(parsed.answer_source || 'ai_knowledge');
          setMessages((prev) =>
            prev.map((m) =>
              m.id === placeholderId
                ? {
                    ...m,
                    citations: parsed.citations || [],
                    hasTenantDocs: parsed.has_tenant_docs || false,
                    answerSource: parsed.answer_source || 'ai_knowledge',
                  }
                : m
            )
          );
        },
        onSuggestions: (questions) => {
          setMessages(prev =>
            prev.map(m =>
              m.id === placeholderId ? { ...m, suggestions: questions } : m
            )
          );
        },
        onError: (errMsg, code) => {
          if (code) dispatch(authApi.util.invalidateTags(['USER']));
          toast.error(errMsg || 'Stream failed. Trying regular send...');
          setIsStreaming(false);
        },
      }
    );

    if (!streamedOk) {
      setMessages((prev) => prev.filter((m) => m.id !== placeholderId));
      try {
        const response = await createNewRoomChat({ user_question: messageText, database_id: selectedDB }).unwrap();
        if (Array.isArray(response.citations)) setCitations(response.citations);
        if (response.has_tenant_docs !== undefined) setHasTenantDocs(response.has_tenant_docs);
        if (response.answer_source) setAnswerSource(response.answer_source);
        citationsRef.current = response.citations || [];
        answerSourceRef.current = response.answer_source || 'ai_knowledge';
        setMessages((prev) => [
          ...prev,
          {
            sender: 'bot',
            text: response.bot_answer || 'No response received.',
            id: response.chat_id,
            feedback: null,
            download_url: response.download_url,
            citations: response.citations || [],
            hasTenantDocs: response.has_tenant_docs ?? false,
            answerSource: response.answer_source || 'ai_knowledge',
            suggestions: response.suggestions || [],
          },
        ]);
        navigate(`/${response.room_id}`, {
          replace: true,
          state: {
            citations: response.citations || [],
            hasTenantDocs: response.has_tenant_docs ?? false,
            answerSource: response.answer_source || 'ai_knowledge',
          },
        });
      } catch (err) {
        if (err?.data?.code) dispatch(authApi.util.invalidateTags(['USER']));
        const status = err?.status;
        if (status === 429) {
          toast.error("You've sent a lot of messages! Please wait a moment before continuing.");
        } else {
          toast.error(err?.message || 'Failed to fetch response.');
        }
      }
    }
    } finally {
      isSendingRef.current = false;
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const result = validateAnalysisFile(file);
    if (!result.ok) {
      toast.error(result.error);
      e.target.value = '';
      return;
    }
    setAnalysisFile(file);
    e.target.value = '';
  };

  const handleAnalyzeDocument = async (query) => {
    if (!analysisFile || !query.trim()) return;

    // Abort any in-flight analysis
    if (analyzeAbortRef.current) {
      analyzeAbortRef.current.abort();
    }
    const abortController = new AbortController();
    analyzeAbortRef.current = abortController;

    setIsAnalyzing(true);
    userScrolledUp.current = false;
    const fileToAnalyze = analysisFile;
    const userMsgId = `user-${Date.now()}`;
    const loadingId = `analysis-${Date.now()}`;

    setMessages((prev) => [
      ...prev,
      { sender: 'user', text: `📄 **${fileToAnalyze.name}**\n\n${query}`, id: userMsgId },
    ]);
    setInput('');
    setMessages((prev) => [
      ...prev,
      { sender: 'bot', text: `Analyzing ${fileToAnalyze.name}...`, id: loadingId, isLoading: true },
    ]);
    scrollToBottom(true);

    try {
      const formData = new FormData();
      formData.append('file', fileToAnalyze);
      formData.append('query', query);

      const token = localStorage.getItem('access_token');
      const baseUrl = (process.env.REACT_APP_BASE_URL || '').replace(/\/$/, '');

      const res = await fetch(`${baseUrl}/analyze-document/`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
        signal: abortController.signal,
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Analysis failed');
      }

      setMessages((prev) =>
        prev.map((m) =>
          m.id === loadingId
            ? {
                ...m,
                text: data.analysis,
                isLoading: false,
                isDocumentAnalysis: true,
                filename: data.filename,
              }
            : m
        )
      );
      setAnalysisFile(null);
      scrollToBottom(true);
      // The backend now creates and returns a room for home-screen analyses.
      // Navigate into it (same as the normal send flow's onDone) so the chat
      // is sidebar-visible and the next message continues this room instead
      // of silently creating an unrelated one (data-loss bug 2026-07-15).
      if (data.room_id) {
        dispatch(userApi.util.invalidateTags(['NEW_CHAT']));
        navigate(`/${data.room_id}`, { replace: true });
      }
    } catch (err) {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === loadingId
            ? { ...m, text: err.message || 'Failed to analyze document. Please try again.', isLoading: false }
            : m
        )
      );
      toast.error(err.message || 'Failed to analyze document.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const copyMessageAsPlainText = (messageId, text) => {
    const plain = stripMarkdownForCopy(text);
    navigator.clipboard.writeText(plain).then(() => {
      setCopiedId(messageId);
      toast.success('Copied to clipboard!');
      setTimeout(() => setCopiedId(null), 2000);
    }).catch(() => {
      copy(plain);
      setCopiedId(messageId);
      toast.success('Copied to clipboard!');
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  const handleStartEdit = (msg) => {
    setEditingMessageId(msg.id);
    setEditText(msg.text);
  };

  const handleCancelEdit = () => {
    setEditingMessageId(null);
    setEditText('');
  };

  const handleSaveEdit = () => {
    const text = editText.trim();
    if (!text || editingMessageId == null || isStreaming) return;

    const editIndex = messages.findIndex((m) => m.id === editingMessageId);
    if (editIndex === -1) return;

    const kept = messages.slice(0, editIndex);
    setEditingMessageId(null);
    setEditText('');
    setMessages(kept);
    handleSendMessage(text);
  };

  const handleFeedback = async (conversationId, isThumbsUp) => {
    try {
      await updateChatFeedback({
        conversation_id: conversationId,
        feedback: { is_thumbs_up: isThumbsUp },
      }).unwrap();
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === conversationId ? { ...msg, feedback: isThumbsUp ? 'thumbs-up' : 'thumbs-down' } : msg
        )
      );
      toast.success('Feedback recorded successfully!');
    } catch {
      toast.error('Failed to submit feedback.');
    }
  };

  const handleChipClick = (question) => {
    setInput(question);
    setTimeout(() => textareaRef.current?.focus(), 0);
  };

  const renderKbEmptyBanner = () => {
    if (kbStatus?.has_documents !== false) return null;
    const isTenantAdmin = (userData?.role || '').toLowerCase() === 'tenant_admin';
    const body = (
      <>
        <span className="kb-empty-icon">💡</span>
        <div className="kb-empty-text">
          <strong>Full Oracle HCM expertise, ready now</strong>
          <span>
            {isTenantAdmin
              ? 'Upload your implementation docs anytime for answers tailored to your setup'
              : 'Ask your workspace admin to add your implementation docs'}
          </span>
        </div>
      </>
    );
    if (isTenantAdmin) {
      return (
        <Link to="/tenant-admin" className="kb-empty-banner kb-empty-banner--link">
          {body}
        </Link>
      );
    }
    return <div className="kb-empty-banner">{body}</div>;
  };

  const renderChatInput = (variant = 'footer') => {
    const isWelcome = variant === 'welcome';
    const inputRow = (
      <div className="chat-input-row">
        {isWelcome ? (
          <Form.Control
            as="textarea"
            rows={1}
            ref={textareaRef}
            placeholder={getAnalysisPlaceholder(analysisFile, inputPlaceholder)}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => setWelcomeInputFocused(true)}
            onBlur={() => setWelcomeInputFocused(false)}
            disabled={isStreaming || isAnalyzing || !canAsk}
            aria-describedby={canAsk ? undefined : ASK_BLOCKED_MESSAGE_ID}
          />
        ) : (
          <div className="input-wrapper input-area">
            <Form.Control
              as="textarea"
              rows={1}
              ref={textareaRef}
              className="custom-input chat-textarea"
              placeholder={getAnalysisPlaceholder(analysisFile, inputPlaceholder)}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isStreaming || isAnalyzing || !canAsk}
              aria-describedby={canAsk ? undefined : ASK_BLOCKED_MESSAGE_ID}
            />
          </div>
        )}
        <button
          type="button"
          className={`attach-btn ${analysisFile ? 'has-file' : ''}`}
          onClick={() => fileInputRef.current?.click()}
          title="Analyze a document"
          disabled={isStreaming || isAnalyzing || !canAsk}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
          </svg>
        </button>
        <button
          type="button"
          className="send-btn chat-send-btn chat-send-btn-inline"
          onClick={() => handleSendMessage()}
          disabled={isStreaming || isAnalyzing || !canAsk}
          aria-label="Send message"
        >
          ↑
        </button>
      </div>
    );

    const inner = (
      <>
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          accept=".pdf,.xlsx,.xls,.csv,.docx"
          onChange={handleFileSelect}
        />
        {analysisFile && (
          <div className="analysis-file-preview">
            <div className="analysis-file-info">
              <span className="analysis-file-icon">📄</span>
              <span className="analysis-file-name">{analysisFile.name}</span>
              <span className="analysis-file-size">
                {(analysisFile.size / 1024).toFixed(0)}KB
              </span>
            </div>
            <button
              type="button"
              className="analysis-file-remove"
              onClick={() => setAnalysisFile(null)}
            >
              ✕
            </button>
          </div>
        )}
        {inputRow}
      </>
    );

    if (isWelcome) {
      return (
        <div className={`cw-welcome-input-wrap ${welcomeInputFocused ? 'focused' : ''}`}>
          {inner}
        </div>
      );
    }
    return <div className="chat-input-wrapper">{inner}</div>;
  };

  return (
    <div className="chat-window chat-area flex flex-col h-full">
      <ChatHeader />
      <PlanBanner />
      {messages.length === 0 ? (
        <div className="cw-welcome-screen">
          <div className="cw-welcome-inner cw-welcome-enter">
            <h1
              className="welcome-greeting cw-welcome-enter-item"
              style={{ '--enter-delay': '0ms' }}
            >
              {getGreeting()}{firstName ? `, ${firstName}` : ''}
            </h1>
            <p
              className="welcome-subtitle cw-welcome-enter-item"
              style={{ '--enter-delay': '60ms' }}
            >
              Your Oracle HCM workspace is ready.
            </p>
            {kbStatus?.has_documents === false && (
              <div
                className="cw-welcome-kb-banner cw-welcome-enter-item"
                style={{ '--enter-delay': '120ms' }}
              >
                {renderKbEmptyBanner()}
              </div>
            )}
            <div
              className="cw-welcome-enter-item"
              style={{ '--enter-delay': kbStatus?.has_documents === false ? '180ms' : '120ms' }}
            >
              {renderChatInput('welcome')}
            </div>
            <div
              className="cw-welcome-chips cw-welcome-enter-item"
              style={{ '--enter-delay': kbStatus?.has_documents === false ? '240ms' : '180ms' }}
            >
              {SUGGESTED_QUESTIONS.map((question) => (
                <button
                  key={question}
                  type="button"
                  className="cw-welcome-chip"
                  onClick={() => handleChipClick(question)}
                  disabled={!canAsk}
                >
                  {question}
                </button>
              ))}
            </div>
            <div
              className="cw-welcome-disclaimer cw-welcome-enter-item"
              style={{ '--enter-delay': kbStatus?.has_documents === false ? '300ms' : '240ms' }}
            >
              <p>Edge AI can make mistakes. Check important info.</p>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div
            id="messageBox"
            ref={chatContainerRef}
            onScroll={handleScroll}
            className="messages messages-container chat-messages-container flex-grow-1 overflow-auto"
          >
            <div className="messages-list">
              {messages.map((msg) => (
              <div
                key={msg.id ?? `${msg.sender}-${msg.text?.slice(0, 20)}`}
                className={`message ${msg.sender === 'user' ? 'message-user user-message' : 'message-bot bot-message'}`}
              >
                {msg.sender === 'user' && editingMessageId === msg.id ? (
                  <div className="user-message-edit">
                    <textarea
                      className="user-edit-textarea"
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      rows={3}
                      autoFocus
                    />
                    <div className="user-edit-actions">
                      <button type="button" className="user-edit-save" onClick={handleSaveEdit} disabled={isStreaming || !canAsk}>
                        Save & Send
                      </button>
                      <button type="button" className="user-edit-cancel" onClick={handleCancelEdit}>
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    {msg.sender === 'bot' ? (
                      <div className="message-bot-wrapper bot-message-row">
                        <div className="bot-avatar" aria-hidden="true">
                          <img src="/edge-ai-thinking-logo.png" alt="" />
                        </div>
                        <div className="message-content bot-content">
                          {msg.isDocumentAnalysis && (
                            <div className="doc-analysis-header">
                              <span className="doc-analysis-icon">📊</span>
                              <span className="doc-analysis-label">
                                Document Analysis: {msg.filename}
                              </span>
                            </div>
                          )}
                          {(msg.streaming && !msg.text) || msg.isLoading ? (
                            <ThinkingIndicator compact />
                          ) : (
                            <>
                              <div className="bot-message-content">
                                <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                                  {msg.text || ''}
                                </ReactMarkdown>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="message-content">
                        {msg.text?.includes('📄 **') ? (
                          <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                            {msg.text}
                          </ReactMarkdown>
                        ) : (
                          msg.text
                        )}
                      </div>
                    )}
                    {msg.sender === 'bot' && msg.download_url && (
                      <a
                        href={`${apiDomain}${msg.download_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="excel-download-btn"
                      >
                        📥 Download Excel File
                      </a>
                    )}
                    {msg.sender === 'bot' && msg.citations?.length > 0 && (
                      <div className="citations-after-response">
                        <Citations
                          citations={msg.citations}
                          hasTenantDocs={msg.hasTenantDocs}
                          answerSource={msg.answerSource}
                        />
                      </div>
                    )}
                    {msg.sender === 'bot' && msg.answerSource === 'verified' && !msg.streaming && (
                      <div className="citations-after-response">
                        <ReviewedAnswerBadge />
                      </div>
                    )}
                    {msg.sender === 'bot' && msg.suggestions?.length > 0 && !msg.streaming && (
                      <div className="suggestion-chips">
                        <span className="suggestion-label">You might ask:</span>
                        {msg.suggestions.map((q, i) => (
                          <button
                            key={i}
                            className="suggestion-chip"
                            onClick={() => {
                              setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, suggestions: [] } : m));
                              handleSendMessage(q);
                            }}
                            disabled={isStreaming || !canAsk}
                          >
                            {q}
                          </button>
                        ))}
                      </div>
                    )}
                    <div className={`message-actions ${msg.sender === 'user' ? 'user-message-actions' : ''}`}>
                      {msg.sender === 'user' ? (
                        <>
                          <button
                            type="button"
                            className={`copy-icon user-action-icon ${copiedId === msg.id ? 'active' : ''}`}
                            aria-label="Copy your message"
                            onClick={() => copyMessageAsPlainText(msg.id, msg.text)}
                          >
                            <Copy aria-hidden="true" focusable="false" />
                          </button>
                          <button
                            type="button"
                            className="edit-icon user-action-icon"
                            aria-label="Edit your message"
                            onClick={() => handleStartEdit(msg)}
                          >
                            <Pencil size={14} aria-hidden="true" focusable="false" />
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            type="button"
                            className={`copy-icon ${copiedId === msg.id ? 'active' : ''}`}
                            aria-label="Copy response"
                            onClick={() => copyMessageAsPlainText(msg.id, msg.text)}
                          >
                            <Copy aria-hidden="true" focusable="false" />
                          </button>
                          <div className="feedback-buttons">
                            <button
                              type="button"
                              className={`thumb-icon ${msg.feedback === 'thumbs-up' ? 'active' : ''}`}
                              aria-label="Mark response as helpful"
                              aria-pressed={msg.feedback === 'thumbs-up'}
                              onClick={() => handleFeedback(msg.id, true)}
                            >
                              <HandThumbsUp aria-hidden="true" focusable="false" />
                            </button>
                            <button
                              type="button"
                              className={`thumb-icon ${msg.feedback === 'thumbs-down' ? 'active' : ''}`}
                              aria-label="Mark response as not helpful"
                              aria-pressed={msg.feedback === 'thumbs-down'}
                              onClick={() => handleFeedback(msg.id, false)}
                            >
                              <HandThumbsDown aria-hidden="true" focusable="false" />
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </>
                )}
              </div>
            ))}

            {citations.length > 0 && (
              <div className="citations-after-response">
                <Citations citations={citations} hasTenantDocs={hasTenantDocs} answerSource={answerSource} />
              </div>
            )}

            {isStreaming && !isAnalyzing && (
              <div className="streaming-dots" aria-hidden="true">
                <span />
                <span />
                <span />
              </div>
            )}
            </div>
          </div>
          <div className="chat-input-footer">
            {renderKbEmptyBanner()}
            {renderChatInput('footer')}
            <div className="chat-disclaimer">
              <p>Edge AI can make mistakes. Check important info.</p>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ChatWindow;
